// frontend/src/pages/Notifications.jsx
// Notifications Page - SRS Section 8
// Shows all in-app notifications + allows HR/Admin to create manual notifications
// with department, employee targeting and priority levels
// Fix: using n.id instead of n._id (backend returns id not _id)

import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck, Trash2, AlertCircle, X, Plus, Send } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAppNotifications } from '../context/AppNotificationsContext';

// ─── Notification type badge color mapping ─────────────────────────────────
const TYPE_STYLES = {
  commission:   { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500'  },
  leave:        { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  payroll:      { bg: 'bg-blue-50',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  asset:        { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  expense:      { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  milestone:    { bg: 'bg-red-50',    text: 'text-red-700',    dot: 'bg-red-500'    },
  lead:         { bg: 'bg-indigo-50', text: 'text-indigo-700', dot: 'bg-indigo-500' },
  announcement: { bg: 'bg-pink-50',   text: 'text-pink-700',   dot: 'bg-pink-500'   },
  general:      { bg: 'bg-gray-50',   text: 'text-gray-700',   dot: 'bg-gray-400'   },
};

// ─── Priority badge styles ─────────────────────────────────────────────────
const PRIORITY_STYLES = {
  high:   { badge: 'bg-red-100 text-red-700'      },
  medium: { badge: 'bg-yellow-100 text-yellow-700' },
  low:    { badge: 'bg-gray-100 text-gray-600'     },
};

// ─── Roles allowed to create notifications ─────────────────────────────────
const CAN_CREATE = ['super_admin', 'hr_manager'];

// ─── Format timestamp to human readable ───────────────────────────────────
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Main Component ────────────────────────────────────────────────────────
const Notifications = () => {
  const { user } = useAuth();
  const {
    notifications, loading, refresh,
    markAsRead: ctxMarkAsRead, markAllAsRead: ctxMarkAllAsRead,
    removeNotification, clearAll: ctxClearAll,
  } = useAppNotifications();

  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [clearing, setClearing] = useState(false);

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sending, setSending] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Create form state
  const [form, setForm] = useState({
    title: '',
    message: '',
    priority: 'medium',
    type: 'announcement',
    target: 'all',
    department_id: '',
    employee_id: '',
  });
  const [formErrors, setFormErrors] = useState({});

  // ─── Fetch departments and employees for create modal dropdowns ────────
  const fetchDropdownData = useCallback(async () => {
    try {
      const [deptData, empData] = await Promise.all([
        api.get('/api/departments'),
        api.get('/api/employees'),
      ]);
      setDepartments(
        Array.isArray(deptData) ? deptData : (deptData?.departments || deptData?.data || [])
      );
      const empList = Array.isArray(empData) ? empData : (empData?.employees || empData?.data || []);
      setEmployees(empList);
    } catch (err) {
      // Silently fail — dropdowns stay empty
    }
  }, []);

  // ─── Auto hide success message after 3 seconds ─────────────────────────
  useEffect(() => {
    if (createSuccess) {
      const t = setTimeout(() => setCreateSuccess(''), 3000);
      return () => clearTimeout(t);
    }
  }, [createSuccess]);

  // ─── Open create modal and load dropdown data ──────────────────────────
  const openCreate = () => {
    fetchDropdownData();
    setForm({
      title: '',
      message: '',
      priority: 'medium',
      type: 'announcement',
      target: 'all',
      department_id: '',
      employee_id: '',
    });
    setFormErrors({});
    setCreateError('');
    setShowCreate(true);
  };

  // ─── Validate create notification form ────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!form.title.trim()) errors.title = 'Title is required';
    if (!form.message.trim()) errors.message = 'Message is required';
    if (form.target === 'department' && !form.department_id)
      errors.department_id = 'Please select a department';
    if (form.target === 'employee' && !form.employee_id)
      errors.employee_id = 'Please select an employee';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ─── Submit create notification ────────────────────────────────────────
  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      setSending(true);
      setCreateError('');
      await api.post('/api/notifications/create', {
        title: form.title,
        message: form.message,
        priority: form.priority,
        type: form.type,
        target: form.target,
        department_id: form.target === 'department' ? form.department_id : undefined,
        employee_id: form.target === 'employee' ? form.employee_id : undefined,
      });
      setShowCreate(false);
      setCreateSuccess('Notification sent successfully');
      refresh();
    } catch (err) {
      setCreateError(err.message || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  // ─── Mark single notification as read ─────────────────────────────────
  const markAsRead = async (notifId) => {
    await ctxMarkAsRead(notifId);
  };

  // ─── Mark all notifications as read ───────────────────────────────────
  const markAllAsRead = async () => {
    await ctxMarkAllAsRead();
  };

  // ─── Delete single notification ────────────────────────────────────────
  const deleteNotification = async (notifId) => {
    await removeNotification(notifId);
  };

  // ─── Clear all notifications ───────────────────────────────────────────
  const clearAll = async () => {
    setClearing(true);
    await ctxClearAll();
    setClearing(false);
  };

  // ─── Filter notifications by tab selection ─────────────────────────────
  const filteredNotifications = notifications.filter((n) => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const canCreate = CAN_CREATE.includes(user?.role);

  const getTypeStyle = (type) => TYPE_STYLES[type] || TYPE_STYLES.general;
  const getPriorityStyle = (priority) => PRIORITY_STYLES[priority] || PRIORITY_STYLES.low;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500 mt-1">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
              : 'All caught up'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Create button — only for HR Manager and Super Admin */}
          {canCreate && (
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Notification
            </button>
          )}
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              onClick={clearAll}
              disabled={clearing}
              className="inline-flex items-center gap-2 text-sm font-medium text-red-500 hover:text-red-600 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── Success Message ── */}
      {createSuccess && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{createSuccess}</span>
        </div>
      )}

      {/* ── Error Message ── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Filter Tabs ── */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[
          { key: 'all',    label: 'All'    },
          { key: 'unread', label: 'Unread' },
          { key: 'read',   label: 'Read'   },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.key === 'unread' && unreadCount > 0 && (
              <span className="ml-1.5 bg-blue-600 text-white text-xs rounded-full px-1.5 py-0.5">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Notifications List ── */}
      {loading ? (
        // Loading skeleton
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-gray-200 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredNotifications.length === 0 ? (
        // Empty state
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bell className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-500 font-medium">
            {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
          </p>
          <p className="text-gray-400 text-sm mt-1">You're all caught up!</p>
        </div>
      ) : (
        // Notifications list
        <div className="space-y-2">
          {filteredNotifications.map((notif) => {
            const typeStyle = getTypeStyle(notif.type);
            const priorityStyle = getPriorityStyle(notif.priority);
            const isHighPriority = notif.priority === 'high';

            return (
              // Using notif.id (not notif._id) — backend returns "id"
              <div
                key={notif.id}
                className={`bg-white rounded-xl border transition-all ${
                  isHighPriority
                    ? 'border-red-200 shadow-sm'
                    : notif.is_read
                    ? 'border-gray-100'
                    : 'border-blue-100 shadow-sm'
                }`}
              >
                <div className="flex items-start gap-3 p-4">
                  {/* Notification type icon dot */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isHighPriority ? 'bg-red-50' : typeStyle.bg
                  }`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      isHighPriority ? 'bg-red-500' : typeStyle.dot
                    }`} />
                  </div>

                  {/* Notification content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {/* Title */}
                        {notif.title && (
                          <p className={`text-sm font-semibold mb-0.5 ${
                            isHighPriority ? 'text-red-700' : 'text-gray-900'
                          }`}>
                            {notif.title}
                          </p>
                        )}
                        {/* Message */}
                        <p className={`text-sm leading-relaxed ${
                          notif.is_read ? 'text-gray-500' : 'text-gray-800'
                        }`}>
                          {notif.message}
                        </p>
                      </div>
                      {/* Unread blue dot */}
                      {!notif.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                      )}
                    </div>

                    {/* Meta row — time, priority badge, type badge */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-400">
                        {formatTime(notif.created_at)}
                      </span>
                      {notif.priority && notif.priority !== 'low' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityStyle.badge}`}>
                          {notif.priority === 'high' ? '🔴 High Priority' : '🟡 Medium'}
                        </span>
                      )}
                      {notif.type && notif.type !== 'general' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeStyle.bg} ${typeStyle.text}`}>
                          {notif.type.charAt(0).toUpperCase() + notif.type.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!notif.is_read && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Mark as read"
                      >
                        <Check className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notif.id)}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          CREATE NOTIFICATION MODAL
      ═══════════════════════════════════════════════════════════════════ */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h2 className="font-semibold text-gray-900">Create Notification</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Send to everyone, a department, or a specific employee
                </p>
              </div>
              <button
                onClick={() => setShowCreate(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">

              {/* Create error alert */}
              {createError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {createError}
                </div>
              )}

              {/* Title field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => {
                    setForm({ ...form, title: e.target.value });
                    if (formErrors.title) setFormErrors({ ...formErrors, title: '' });
                  }}
                  placeholder="e.g. Office Closed Tomorrow"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                    formErrors.title ? 'border-red-400' : 'border-gray-200'
                  }`}
                />
                {formErrors.title && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>
                )}
              </div>

              {/* Message field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => {
                    setForm({ ...form, message: e.target.value });
                    if (formErrors.message) setFormErrors({ ...formErrors, message: '' });
                  }}
                  placeholder="Write your notification message here..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none ${
                    formErrors.message ? 'border-red-400' : 'border-gray-200'
                  }`}
                />
                {formErrors.message && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>
                )}
              </div>

              {/* Priority and Type row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">🔴 High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  >
                    <option value="announcement">Announcement</option>
                    <option value="leave">Leave</option>
                    <option value="payroll">Payroll</option>
                    <option value="asset">Asset</option>
                    <option value="expense">Expense</option>
                    <option value="general">General</option>
                  </select>
                </div>
              </div>

              {/* Send To target selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'all',        label: 'Everyone'   },
                    { key: 'department', label: 'Department' },
                    { key: 'employee',   label: 'Employee'   },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() =>
                        setForm({ ...form, target: opt.key, department_id: '', employee_id: '' })
                      }
                      className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        form.target === opt.key
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Department dropdown — visible when target is department */}
              {form.target === 'department' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.department_id}
                    onChange={(e) => {
                      setForm({ ...form, department_id: e.target.value });
                      if (formErrors.department_id)
                        setFormErrors({ ...formErrors, department_id: '' });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                      formErrors.department_id ? 'border-red-400' : 'border-gray-200'
                    }`}
                  >
                    <option value="">-- Select Department --</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.department_id && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.department_id}</p>
                  )}
                </div>
              )}

              {/* Employee dropdown — visible when target is employee */}
              {form.target === 'employee' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.employee_id}
                    onChange={(e) => {
                      setForm({ ...form, employee_id: e.target.value });
                      if (formErrors.employee_id)
                        setFormErrors({ ...formErrors, employee_id: '' });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                      formErrors.employee_id ? 'border-red-400' : 'border-gray-200'
                    }`}
                  >
                    <option value="">-- Select Employee --</option>
                    {employees.map((emp) => (
                      <option key={emp._id} value={emp._id}>
                        {emp.full_name || emp.name} — {emp.employee_id}
                      </option>
                    ))}
                  </select>
                  {formErrors.employee_id && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.employee_id}</p>
                  )}
                </div>
              )}

              {/* High priority warning banner */}
              {form.priority === 'high' && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  This notification will appear highlighted in red for all recipients
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
              <button
                onClick={() => setShowCreate(false)}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={sending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-60"
              >
                {sending ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Send Notification
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;